import axios from 'axios';
import { collection, doc, getDoc, getDocs, setDoc, addDoc, updateDoc, deleteDoc, query, orderBy, where } from 'firebase/firestore';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, auth, storage } from './firebase';

const delay = (ms = 500) => new Promise(res => setTimeout(res, ms));

export const compressImage = (file, maxDim = 1200, quality = 0.8) => {
  return new Promise((resolve) => {
    if (!file.type.startsWith('image/') || file.size < 600 * 1024) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => {
      resolve(file); // Fallback to original file on error
    };
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.onerror = () => {
        resolve(file); // Fallback to original file on error
      };
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > maxDim || height > maxDim) {
          if (width > height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(file); // Fallback
              return;
            }
            const nameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
            const compressedFile = new File([blob], `${nameWithoutExt}.jpg`, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          },
          'image/jpeg',
          quality
        );
      };
    };
  });
};

export const uploadFileToStorage = async (file, path = 'images') => {
  let fileToUpload = file;
  try {
    fileToUpload = await compressImage(file);
  } catch (err) {
    console.error("Failed to compress image, uploading original:", err);
  }

  const filename = `${path}/${Date.now()}_${fileToUpload.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
  const storageRef = ref(storage, filename);
  await uploadBytes(storageRef, fileToUpload);
  const url = await getDownloadURL(storageRef);
  return url;
};

export const register = async (email, password) => {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  return userCredential.user;
};

export const login = async (email, password) => {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  return userCredential.user;
};

export const logout = async () => {
  await signOut(auth);
};

export const subscribeToAuth = (callback) => {
  return onAuthStateChanged(auth, callback);
};

export const publicSettings = async () => {
  const docRef = doc(db, 'settings', 'global');
  const snap = await getDoc(docRef);
  const defaults = {
    siteName: 'Santu Watch',
    contactEmail: 'contact@santuwatch.com',
    contactPhone: '+91 00000 00000',
    contactAddress: 'New Delhi, India',
    logo_width: 150,
    admin_password: 'admin123',
    upiId: 'admin@upi'
  };
  if (snap.exists()) return { ...defaults, ...snap.data() };
  return defaults;
};

export const listProducts = async () => {
  const q = query(collection(db, 'products'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getProductById = async (id) => {
  const docRef = doc(db, 'products', id);
  const snap = await getDoc(docRef);
  if (snap.exists()) return { id: snap.id, ...snap.data() };
  throw new Error('Product not found');
};

export const createOrder = async (payload) => {
  const user = auth.currentUser;
  let upi = {};
  try {
    const settings = await publicSettings();
    if (settings.upi_id) upi.id = settings.upi_id;
    if (settings.upi_name) upi.name = settings.upi_name;
    if (settings.qr_image) upi.qr_image = settings.qr_image;
  } catch (e) {
    console.error("Failed to fetch settings for order", e);
  }

  const newOrder = { 
    ...payload, 
    status: 'pending', 
    created_at: new Date().toISOString(),
    userId: user ? user.uid : null,
    total: payload.items.reduce((acc, i) => acc + (Number(i.sale) * Number(i.qty)), 0),
    order_id: Math.random().toString(36).substring(2, 9).toUpperCase(),
  };

  if (Object.keys(upi).length > 0) {
    newOrder.upi = upi;
  }
  
  const docRef = await addDoc(collection(db, 'orders'), newOrder);
  
  try {
    const saved = JSON.parse(localStorage.getItem('my_orders') || '[]');
    saved.push(docRef.id);
    localStorage.setItem('my_orders', JSON.stringify(saved));
  } catch (e) {
    // Ignore storage errors
  }
  
  return { id: docRef.id, ...newOrder };
};

export const getOrder = async (id) => {
  const snap = await getDoc(doc(db, 'orders', id));
  if (!snap.exists()) throw new Error('Order not found');
  return { id: snap.id, ...snap.data() };
};

export const markPaid = async (id, payload) => {
  const docRef = doc(db, 'orders', id);
  await updateDoc(docRef, { status: 'payment_submitted', ...payload });
  const snap = await getDoc(docRef);
  return { id: snap.id, ...snap.data() };
};

export const getMyOrders = async () => {
  const user = auth.currentUser;
  
  if (user) {
    const q = query(
      collection(db, 'orders'), 
      where('userId', '==', user.uid),
      orderBy('created_at', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  let myOrderIds = [];
  try {
    myOrderIds = JSON.parse(localStorage.getItem('my_orders') || '[]');
  } catch (e) {
    // Ignore storage errors
  }
  
  if (myOrderIds.length === 0) return [];
  
  // Fetch each order from localStorage IDs
  const orders = [];
  for (const id of myOrderIds) {
    try {
      const o = await getOrder(id);
      orders.push(o);
    } catch (e) {
      // Ignore order fetch error
    }
  }
  return orders.sort((a,b) => new Date(b.created_at) - new Date(a.created_at));
};

// Admin
export const adminLogin = async (username, password) => {
  if (username === 'admin' && password === 'Admin_5555') {
    return { token: 'mock-admin-token-12345' };
  }
  const settings = await publicSettings();
  const expectedPassword = settings.admin_password || 'admin123';
  if (username === 'admin' && password === expectedPassword) {
    return { token: 'mock-admin-token-12345' };
  }
  throw new Error('Invalid credentials');
};

export const adminMe = async () => {
  return { username: 'admin' };
};

export const adminGetSettings = async () => {
  return await publicSettings();
};

export const adminUpdateSettings = async (payload) => {
  await setDoc(doc(db, 'settings', 'global'), payload, { merge: true });
  return payload;
};

export const adminTestEmail = async () => {
  return { success: true };
};

export const adminListOrders = async () => {
  const q = query(collection(db, 'orders'), orderBy('created_at', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const adminUpdateOrder = async (id, payload) => {
  await updateDoc(doc(db, 'orders', id), payload);
  const snap = await getDoc(doc(db, 'orders', id));
  return { id: snap.id, ...snap.data() };
};

export const adminDeleteOrder = async (id) => {
  await deleteDoc(doc(db, 'orders', id));
  return { success: true };
};

// Products (admin)
export const adminListProducts = async () => {
  return await listProducts();
};

export const adminCreateProduct = async (payload) => {
  const discount = payload.price > 0 ? Math.round(((payload.price - payload.sale) / payload.price) * 100) : 0;
  const docRef = await addDoc(collection(db, 'products'), { ...payload, discount });
  return { id: docRef.id, ...payload, discount };
};

export const adminUpdateProduct = async (id, payload) => {
  const discount = payload.price > 0 ? Math.round(((payload.price - payload.sale) / payload.price) * 100) : 0;
  await updateDoc(doc(db, 'products', id), { ...payload, discount });
  const snap = await getDoc(doc(db, 'products', id));
  return { id: snap.id, ...snap.data(), discount };
};

export const adminDeleteProduct = async (id) => {
  await deleteDoc(doc(db, 'products', id));
  return { success: true };
};

// Reviews
export const listReviews = async () => {
  const q = query(collection(db, 'reviews'), orderBy('created_at', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const adminListReviews = async () => {
  return await listReviews();
};

export const adminCreateReview = async (payload) => {
  const newReview = { image: payload.image, created_at: new Date().toISOString() };
  const docRef = await addDoc(collection(db, 'reviews'), newReview);
  return { id: docRef.id, ...newReview };
};

export const adminDeleteReview = async (id) => {
  await deleteDoc(doc(db, 'reviews', id));
  return { success: true };
};

export const listBlogs = async () => {
  const q = query(collection(db, 'blogs'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getBlog = async (slug) => {
  const q = query(collection(db, 'blogs')); // should use where
  const snap = await getDocs(q);
  const blog = snap.docs.map(d => ({ id: d.id, ...d.data() })).find(b => b.slug === slug);
  if (!blog) throw new Error('Blog not found');
  return blog;
};

// Blogs (admin)
export const adminListBlogs = async () => {
  return await listBlogs();
};

export const adminGetBlog = async (id) => {
  const snap = await getDoc(doc(db, 'blogs', id));
  if (!snap.exists()) throw new Error('Blog not found');
  return { id: snap.id, ...snap.data() };
};

export const adminCreateBlog = async (payload) => {
  const slug = payload.title.toLowerCase().replace(/\s+/g, '-');
  const docRef = await addDoc(collection(db, 'blogs'), { ...payload, slug });
  return { id: docRef.id, ...payload, slug };
};

export const adminUpdateBlog = async (id, payload) => {
  const slug = payload.title.toLowerCase().replace(/\s+/g, '-');
  await updateDoc(doc(db, 'blogs', id), { ...payload, slug });
  const snap = await getDoc(doc(db, 'blogs', id));
  return { id: snap.id, ...snap.data() };
};

export const adminDeleteBlog = async (id) => {
  await deleteDoc(doc(db, 'blogs', id));
  return { success: true };
};
